import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth, db } from '../../firebase/config';
import { Box, Typography, CircularProgress, Alert, Container, Paper } from '@mui/material';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Page d'authentification pour les utilisateurs venant du PlayStore
 * Récupère les paramètres depuis l'URL et authentifie l'utilisateur
 */
const PlayStoreAuth: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const authenticateFromUrl = async () => {
      try {
        // Récupérer les paramètres depuis l'URL
        const params = new URLSearchParams(location.search);
        const uid = params.get('uid');
        const email = params.get('email');
        const role = params.get('role') || 'coach';
        const source = params.get('source');
        const redirectUrl = params.get('redirectUrl') || '/';
        
        if (!uid || !email) {
          throw new Error('Paramètres manquants dans l\'URL (uid ou email)');
        }
        
        // Générer un mot de passe temporaire basé sur l'uid (pour l'authentification par email)
        const tempPassword = `temp_${uid.substring(0, 8)}`;
        
        try {
          // Essayer de se connecter d'abord
          await signInWithEmailAndPassword(auth, email, tempPassword);
        } catch (signInError) {
          // Si la connexion échoue, créer un nouvel utilisateur
          console.log('Création d\'un nouvel utilisateur...');
          await createUserWithEmailAndPassword(auth, email, tempPassword);
          
          // Mettre à jour le profil si nécessaire
          if (auth.currentUser) {
            await updateProfile(auth.currentUser, {
              displayName: email.split('@')[0]
            });
          }
        }
        
        // Vérifier si l'utilisateur existe dans Firestore
        const userRef = doc(db, 'users', uid);
        const userDoc = await getDoc(userRef);
        
        // Créer ou mettre à jour le document utilisateur avec un profil plus complet
        await setDoc(userRef, {
          email,
          uid,
          role,
          source: source || 'playstore',
          isActive: true,
          lastLogin: serverTimestamp(),
          updatedAt: serverTimestamp(),
          ...(userDoc.exists() ? {} : { 
            createdAt: serverTimestamp(),
            displayName: email.split('@')[0],
            firstName: email.split('@')[0],
            lastName: '',
            photoURL: '',
            emailVerified: false,
            preferences: {
              language: 'fr',
              notifications: true,
              theme: 'light'
            },
            subscriptions: []
          })
        }, { merge: true });
        
        // Rediriger vers l'URL spécifiée ou la page d'accueil
        console.log('Authentification réussie, redirection vers:', redirectUrl);
        navigate(redirectUrl, { replace: true });
      } catch (error: any) {
        console.error('Erreur d\'authentification:', error);
        setError(`Échec de l'authentification: ${error.message}`);
        setLoading(false);
      }
    };
    
    // Vérifier si nous avons des paramètres d'authentification dans l'URL
    const params = new URLSearchParams(location.search);
    if (params.has('uid') && params.has('email')) {
      authenticateFromUrl();
    } else {
      setLoading(false);
      setError('Aucun paramètre d\'authentification trouvé dans l\'URL');
    }
  }, [location.search, navigate]);
  
  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Authentification PlayStore
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
            <CircularProgress size={60} thickness={4} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Connexion en cours...
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </Paper>
    </Container>
  );
};

export default PlayStoreAuth;
